/**
 * Utility functions for flow canvas operations
 */

import { Node, Edge } from '@xyflow/react';
import dagre from 'dagre';

/**
 * Auto-layout nodes using dagre algorithm
 * @param nodes - Array of nodes to layout
 * @param edges - Array of edges defining connections
 * @param direction - Layout direction ('TB' = top-bottom, 'LR' = left-right)
 * @returns Array of positioned nodes
 */
export function autoLayoutNodes(
  nodes: Node[],
  edges: Edge[],
  direction: 'TB' | 'LR' | 'BT' | 'RL' = 'TB'
): Node[] {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  
  const nodeWidth = 300;
  const nodeHeight = 150;
  
  dagreGraph.setGraph({
    rankdir: direction,
    ranksep: 100,
    nodesep: 80,
  });

  // Add nodes to dagre
  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  // Add edges to dagre
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Calculate layout
  dagre.layout(dagreGraph);

  // Apply positions to nodes
  return nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      },
    };
  });
}

/**
 * Generate a unique node ID
 * @param prefix - Prefix for the ID
 * @returns Unique node ID
 */
export function generateNodeId(prefix: string = 'node'): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Generate a unique edge ID
 * @param source - Source node ID
 * @param target - Target node ID
 * @returns Unique edge ID
 */
export function generateEdgeId(source: string, target: string): string {
  return `edge-${source}-${target}-${Date.now()}`;
}

/**
 * Check if there are changes between two node arrays
 * @param nodes1 - First array of nodes
 * @param nodes2 - Second array of nodes
 * @returns True if there are changes
 */
export function hasNodeChanges(nodes1: Node[], nodes2: Node[]): boolean {
  if (nodes1.length !== nodes2.length) return true;
  
  // Simple JSON comparison (can be optimized for production)
  return JSON.stringify(nodes1) !== JSON.stringify(nodes2);
}

/**
 * Check if there are changes between two edge arrays
 * @param edges1 - First array of edges
 * @param edges2 - Second array of edges
 * @returns True if there are changes
 */
export function hasEdgeChanges(edges1: Edge[], edges2: Edge[]): boolean {
  if (edges1.length !== edges2.length) return true;
  
  // Simple JSON comparison (can be optimized for production)
  return JSON.stringify(edges1) !== JSON.stringify(edges2);
}

/**
 * Find cycles in the flow graph
 * @param nodes - Array of nodes
 * @param edges - Array of edges
 * @returns Array of cycles (each cycle is an array of node IDs)
 */
export function findCycles(nodes: Node[], edges: Edge[]): string[][] {
  const cycles: string[][] = [];
  const visited = new Set<string>();
  const recursionStack = new Set<string>();
  
  // Build adjacency list
  const adjacency = new Map<string, string[]>();
  nodes.forEach((node) => {
    adjacency.set(node.id, []);
  });
  edges.forEach((edge) => {
    const neighbors = adjacency.get(edge.source) || [];
    neighbors.push(edge.target);
    adjacency.set(edge.source, neighbors);
  });
  
  function dfs(nodeId: string, path: string[]): boolean {
    visited.add(nodeId);
    recursionStack.add(nodeId);
    path.push(nodeId);
    
    const neighbors = adjacency.get(nodeId) || [];
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        if (dfs(neighbor, [...path])) {
          return true;
        }
      } else if (recursionStack.has(neighbor)) {
        // Cycle detected
        const cycleStart = path.indexOf(neighbor);
        cycles.push(path.slice(cycleStart));
        return true;
      }
    }
    
    recursionStack.delete(nodeId);
    return false;
  }
  
  nodes.forEach((node) => {
    if (!visited.has(node.id)) {
      dfs(node.id, []);
    }
  });
  
  return cycles;
}

/**
 * Get all disconnected nodes (nodes with no incoming or outgoing edges)
 * @param nodes - Array of nodes
 * @param edges - Array of edges
 * @returns Array of disconnected node IDs
 */
export function getDisconnectedNodes(nodes: Node[], edges: Edge[]): string[] {
  const connectedNodes = new Set<string>();
  
  edges.forEach((edge) => {
    connectedNodes.add(edge.source);
    connectedNodes.add(edge.target);
  });
  
  return nodes
    .filter((node) => !connectedNodes.has(node.id))
    .map((node) => node.id);
}

/**
 * Validate handle connection compatibility
 * @param sourceHandle - Source handle position
 * @param targetHandle - Target handle position
 * @returns True if connection is valid
 */
export function validateHandleConnection(
  sourceHandle: string | null | undefined,
  targetHandle: string | null | undefined
): boolean {
  if (!sourceHandle || !targetHandle) return true; // Allow if handles not specified
  
  // Special case: option handles (option-0, option-1, etc.) can connect to left handles
  // Options are positioned on the right side of dialog field nodes
  if (sourceHandle.startsWith('option-') && targetHandle === 'left') {
    return true;
  }
  
  const validConnections: Record<string, string[]> = {
    'right': ['left'],
    'bottom': ['top'],
    'left': ['right'],
    'top': ['bottom'],
  };
  
  const validTargets = validConnections[sourceHandle];
  return validTargets ? validTargets.includes(targetHandle) : false;
}

